import { getApiUrl } from './api-url-helper';
import { isConsentFirstCountry, shouldShowDoNotSellLinkFromCountry, } from './privacyJurisdiction';
const STORAGE_KEY = 'mypace_privacy_geo_v1';
const TTL_MS = 60 * 60 * 1000;
function getBrowserRegionCountryCode() {
    if (typeof navigator === 'undefined')
        return null;
    const candidates = [...(navigator.languages || []), navigator.language].filter(Boolean);
    for (const tag of candidates) {
        const parts = tag.split(/[-_]/);
        if (parts.length < 2)
            continue;
        const region = parts[parts.length - 1].toUpperCase();
        if (region.length === 2 && /^[A-Z]{2}$/.test(region)) {
            return region;
        }
    }
    return null;
}
function isCacheFresh(entry) {
    return Date.now() - entry.ts <= TTL_MS;
}
export function readPrivacyGeoCache() {
    if (typeof window === 'undefined')
        return null;
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.ts !== 'number' ||
            typeof parsed.consentFirstDefault !== 'boolean' ||
            typeof parsed.showDoNotSellLink !== 'boolean') {
            return null;
        }
        if (!isCacheFresh(parsed)) {
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    }
    catch {
        return null;
    }
}
/**
 * Normalise `/location/timezone-by-ip` payload (or `{ countryCode }` only) and persist privacy defaults.
 */
export function applyPrivacyContextFromTimezoneApiData(apiData) {
    if (typeof window === 'undefined')
        return;
    if (!apiData || typeof apiData !== 'object') {
        const browser = getBrowserRegionCountryCode();
        if (browser) {
            applyPrivacyContextFromTimezoneApiData({ countryCode: browser });
        }
        return;
    }
    const d = apiData;
    const privacyRaw = d.privacy;
    let consentFirst;
    let showDns;
    if (privacyRaw && typeof privacyRaw === 'object') {
        const p = privacyRaw;
        if (typeof p.consentFirstDefault === 'boolean')
            consentFirst = p.consentFirstDefault;
        if (typeof p.showDoNotSellLink === 'boolean')
            showDns = p.showDoNotSellLink;
    }
    const ccRaw = d.countryCode;
    let countryCode = typeof ccRaw === 'string' && ccRaw.length >= 2 ? ccRaw.slice(0, 2).toUpperCase() : null;
    if (consentFirst === undefined && countryCode) {
        consentFirst = isConsentFirstCountry(countryCode);
    }
    if (showDns === undefined && countryCode) {
        showDns = shouldShowDoNotSellLinkFromCountry(countryCode);
    }
    if (!countryCode) {
        countryCode = getBrowserRegionCountryCode();
        if (consentFirst === undefined && countryCode) {
            consentFirst = isConsentFirstCountry(countryCode);
        }
        if (showDns === undefined && countryCode) {
            showDns = shouldShowDoNotSellLinkFromCountry(countryCode);
        }
    }
    if (consentFirst === undefined)
        consentFirst = false;
    if (showDns === undefined)
        showDns = false;
    const payload = {
        countryCode,
        consentFirstDefault: consentFirst,
        showDoNotSellLink: showDns,
        ts: Date.now(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('mypace:privacy-geo-updated'));
}
let privacyGeoLoadInFlight = null;
/**
 * Single shared fetch for geo-derived privacy flags (CF-IPCountry on the API). Deduplicates parallel callers.
 */
export function loadPrivacyGeoOnce() {
    if (typeof window === 'undefined')
        return Promise.resolve(null);
    const cached = readPrivacyGeoCache();
    if (cached)
        return Promise.resolve(cached);
    if (privacyGeoLoadInFlight)
        return privacyGeoLoadInFlight;
    privacyGeoLoadInFlight = (async () => {
        const controller = new AbortController();
        const tid = window.setTimeout(() => controller.abort(), 4000);
        try {
            const res = await fetch(getApiUrl('/location/timezone-by-ip'), { signal: controller.signal });
            if (!res.ok) {
                const browser = getBrowserRegionCountryCode();
                if (browser)
                    applyPrivacyContextFromTimezoneApiData({ countryCode: browser });
                return readPrivacyGeoCache();
            }
            const json = await res.json();
            applyPrivacyContextFromTimezoneApiData(json?.data);
            return readPrivacyGeoCache();
        }
        catch {
            const browser = getBrowserRegionCountryCode();
            if (browser)
                applyPrivacyContextFromTimezoneApiData({ countryCode: browser });
            return readPrivacyGeoCache();
        }
        finally {
            window.clearTimeout(tid);
            privacyGeoLoadInFlight = null;
        }
    })();
    return privacyGeoLoadInFlight;
}
